import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/site/SEO";
import { Button } from "@/components/ui/button";
import { useReveal } from "@/hooks/useReveal";
import { SERVICES, SERVICE_CATEGORIES } from "@/lib/services";
import { CALENDLY_URL } from "@/lib/constants";

export default function Leistungen() {
  useReveal();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Leistungen – Versicherungen & Vorsorge im Überblick | FINABLE"
        description="Privathaftpflicht, Hausrat, Wohngebäude, KFZ, Unfall & mehr – wir vergleichen Top-Tarife und finden Deinen passenden Schutz."
        canonical="https://finable.de/leistungen"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "FINABLE Leistungen",
          url: "https://finable.de/leistungen",
          inLanguage: "de-DE",
          numberOfItems: SERVICES.length,
          itemListElement: SERVICES.map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://finable.de/leistungen/${s.slug}`,
            name: s.name,
          })),
        }}
      />
      <Header />
      <main>
        <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="container relative text-center max-w-3xl">
            <span className="reveal inline-block text-accent font-semibold uppercase tracking-wider text-sm">
              Unsere Leistungen
            </span>
            <h1 className="reveal mt-4 font-display text-4xl md:text-6xl font-extrabold text-balance leading-[1.05]">
              Versicherungen, die wirklich <span className="text-accent">schützen</span>
            </h1>
            <p className="reveal mt-6 text-lg md:text-xl text-white/80 leading-relaxed">
              Wir vergleichen den Markt und finden für Dich den Tarif mit dem besten Preis-Leistungs-Verhältnis – unabhängig und transparent.
            </p>
          </div>
        </section>

        {SERVICE_CATEGORIES.map((cat) => (
          <section key={cat.name} className="py-16 md:py-24 bg-background">
            <div className="container">
              <div className="reveal max-w-2xl">
                <h2 className="font-display text-3xl md:text-4xl font-bold text-primary">{cat.name}</h2>
                <p className="mt-3 text-muted-foreground">{cat.description}</p>
              </div>

              <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {SERVICES.filter((s) => s.category === cat.name).map((s) => {
                  const Icon = s.icon;
                  return (
                    <Link
                      key={s.slug}
                      to={`/leistungen/${s.slug}`}
                      className="reveal group relative rounded-2xl bg-card border border-border p-6 shadow-card hover:shadow-elegant hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
                    >
                      <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-accent/5 group-hover:bg-accent/15 transition-colors" />
                      <div className="relative w-12 h-12 rounded-xl bg-gradient-accent text-accent-foreground flex items-center justify-center shadow-glow">
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="relative mt-5 font-display text-xl font-bold text-primary">{s.shortName}</h3>
                      <p className="relative mt-2 text-muted-foreground leading-relaxed flex-1">{s.tagline}</p>
                      <span className="relative mt-5 inline-flex items-center gap-2 text-sm font-semibold text-accent">
                        Mehr erfahren <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        ))}

        <section className="py-16 md:py-20 bg-secondary/40">
          <div className="container text-center reveal">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary">
              Du suchst eine persönliche Beratung?
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              Unsere Leistungen decken Altersvorsorge, Investments, Berufsunfähigkeit und vieles mehr ab. Vereinbare ein kostenloses Erstgespräch – ehrlich, unabhängig und auf Augenhöhe.
            </p>
            <div className="mt-8">
              <Button asChild variant="accent" size="xl">
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                  Erstgespräch vereinbaren <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
